/**
 * SharedShortlist.jsx
 *
 * Public page (no auth required) for viewing an AI-curated gift shortlist
 * shared via a tokenized link. Recipients can cast one lightweight upvote
 * per product. Voter identity is a random ID persisted in localStorage so
 * a person's votes are stable across page reloads without ever needing an
 * account.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Loader2, Sparkles, ThumbsUp, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ProductCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { logger } from '@/utils/logger';

const VOTER_ID_KEY = 'mementa_shared_voter_id';

function getVoterId() {
  let id = localStorage.getItem(VOTER_ID_KEY);
  if (!id) {
    id = `v_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem(VOTER_ID_KEY, id);
  }
  return id;
}

function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function SharedShortlist() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [shortlist, setShortlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [myVotes, setMyVotes] = useState(new Set()); // product_ids I've upvoted

  const creatorName = shortlist?.creator_name || 'A friend';
  const recipientName = shortlist?.recipient_name || '';
  const introLine = recipientName
    ? `${creatorName} curated these gift ideas for ${recipientName}`
    : `${creatorName} curated these gift ideas for you`;

  useDocumentMeta({
    title: shortlist
      ? `${introLine}, Mementa`
      : 'Shared gift shortlist, Mementa',
    description: shortlist
      ? `${introLine}. Tap the picks you like most.`
      : 'View an AI-curated gift shortlist shared with you on Mementa.',
    type: 'website',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${api}/shared-shortlists/${token}`);
        if (!res.ok) {
          if (cancelled) return;
          if (res.status === 410) {
            setExpired(true);
          } else if (res.status === 404) {
            setError('This shortlist could not be found.');
          } else {
            setError('Failed to load shortlist.');
          }
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setShortlist(data);

        // Restore voter's previous votes from localStorage.
        const stored = JSON.parse(localStorage.getItem(`mementa_votes_${token}`) || '[]');
        setMyVotes(new Set(stored));
      } catch (err) {
        logger.error('Failed to load shortlist:', err);
        if (!cancelled) setError('Failed to load shortlist.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const persistMyVotes = (next) => {
    localStorage.setItem(`mementa_votes_${token}`, JSON.stringify(Array.from(next)));
  };

  const handleVote = async (productId) => {
    const voterId = getVoterId();
    // Optimistic UI
    const wasVoted = myVotes.has(productId);
    const nextVotes = new Set(myVotes);
    if (wasVoted) nextVotes.delete(productId); else nextVotes.add(productId);
    setMyVotes(nextVotes);
    persistMyVotes(nextVotes);

    setShortlist((prev) => {
      if (!prev) return prev;
      const counts = { ...(prev.vote_counts || {}) };
      counts[productId] = Math.max(0, (counts[productId] || 0) + (wasVoted ? -1 : 1));
      return { ...prev, vote_counts: counts };
    });

    try {
      const res = await fetch(`${api}/shared-shortlists/${token}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, voter_id: voterId }),
      });
      if (!res.ok) throw new Error(`Vote failed (${res.status})`);
      const data = await res.json();
      // Reconcile with server truth.
      setShortlist((prev) => {
        if (!prev) return prev;
        const counts = { ...(prev.vote_counts || {}), [productId]: data.count };
        return { ...prev, vote_counts: counts };
      });
      if (data.voted !== !wasVoted) {
        const reconciled = new Set(nextVotes);
        if (data.voted) reconciled.add(productId); else reconciled.delete(productId);
        setMyVotes(reconciled);
        persistMyVotes(reconciled);
      }
    } catch (err) {
      logger.error('Vote failed:', err);
      toast.error('Could not save your reaction, try again');
      // Roll back optimistic update.
      setMyVotes(myVotes);
      persistMyVotes(myVotes);
      setShortlist((prev) => {
        if (!prev) return prev;
        const counts = { ...(prev.vote_counts || {}) };
        counts[productId] = Math.max(0, (counts[productId] || 0) + (wasVoted ? 1 : -1));
        return { ...prev, vote_counts: counts };
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFAF5' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#9E6752' }} />
          <p className="text-gray-600">Loading shortlist…</p>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <ExpiredOrMissing
        title="This shortlist has expired"
        message="Shared gift shortlists on Mementa are available for 7 days. Ask the sender to create a fresh link, or explore our curated picks to find your own."
        navigate={navigate}
      />
    );
  }

  if (error) {
    return (
      <ExpiredOrMissing
        title="Shortlist not found"
        message={error}
        navigate={navigate}
      />
    );
  }

  const remaining = daysLeft(shortlist?.expires_at);
  const products = shortlist?.products || [];
  const voteCounts = shortlist?.vote_counts || {};

  return (
    <div className="min-h-screen page-enter" style={{ background: '#FFFAF5' }}>
      {/* Header banner */}
      <div style={{ background: 'white', borderBottom: '1px solid #E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate('/browse')}
            className="flex items-center gap-2 text-sm mb-4 hover:underline"
            style={{ color: '#73766A' }}
            data-testid="shared-shortlist-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse Mementa
          </button>

          <div className="flex items-start gap-4">
            <div
              className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: '#FED7A5' }}
            >
              <Sparkles className="w-6 h-6" style={{ color: '#9E6752' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2"
                style={{ color: '#9E6752' }}
              >
                AI-Curated Gift Shortlist
              </p>
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight"
                style={{ color: '#20212B', fontFamily: 'Fraunces, serif' }}
                data-testid="shared-shortlist-title"
              >
                {introLine}
              </h1>
              {shortlist?.personality_summary && (
                <p
                  className="mt-3 text-sm sm:text-base leading-relaxed"
                  style={{ color: '#534145', maxWidth: '48rem' }}
                  data-testid="shared-shortlist-summary"
                >
                  {shortlist.personality_summary}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs" style={{ color: '#73766A' }}>
                <span className="inline-flex items-center gap-1.5">
                  <Heart className="w-3.5 h-3.5" />
                  {products.length} {products.length === 1 ? 'pick' : 'picks'}
                </span>
                {remaining !== null && (
                  <span className="inline-flex items-center gap-1.5" data-testid="shared-shortlist-expiry">
                    <Clock className="w-3.5 h-3.5" />
                    {remaining > 0
                      ? `Link expires in ${remaining} day${remaining === 1 ? '' : 's'}`
                      : 'Expires today'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Helper strip */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div
          className="rounded-xl px-4 py-3 flex items-center gap-2 text-sm"
          style={{ background: '#FFF8F0', border: '1px solid #FED7A5', color: '#534145' }}
        >
          <ThumbsUp className="w-4 h-4 flex-shrink-0" style={{ color: '#9E6752' }} />
          <span>
            Tap the heart on any pick you love, {creatorName} will see which
            ones resonated. No account needed.
          </span>
        </div>
      </div>

      {/* Product grid with upvote badges */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {products.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold mb-2" style={{ color: '#20212B' }}>
              No picks available
            </h2>
            <p className="text-gray-600 mb-6">
              The items on this shortlist are no longer available.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {products.map((product) => {
              const count = voteCounts[product.id] || 0;
              const iVoted = myVotes.has(product.id);
              return (
                <div key={product.id} className="relative" data-testid={`shared-product-${product.id}`}>
                  <ProductCard
                    product={product}
                    onQuickView={() => {}}
                    isInWishlist={false}
                    onWishlistToggle={() =>
                      toast.info('Sign up on Mementa to save gifts to your own wishlist')
                    }
                  />
                  <button
                    onClick={() => handleVote(product.id)}
                    aria-pressed={iVoted}
                    aria-label={iVoted ? 'Remove upvote' : 'Upvote this pick'}
                    data-testid={`shared-product-vote-${product.id}`}
                    className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-md transition-transform hover:scale-105"
                    style={{
                      background: iVoted ? '#9E6752' : 'white',
                      color: iVoted ? '#FFFAF5' : '#20212B',
                      border: `1.5px solid ${iVoted ? '#9E6752' : '#E5D5C5'}`,
                    }}
                  >
                    <ThumbsUp
                      className="w-3.5 h-3.5"
                      fill={iVoted ? 'currentColor' : 'none'}
                    />
                    {iVoted ? 'You like this' : 'Love it'}
                    {count > 0 && (
                      <span
                        className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px]"
                        style={{
                          background: iVoted ? '#FED7A5' : '#FED7A5',
                          color: '#20212B',
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA Footer */}
      <div style={{ background: 'white', borderTop: '1px solid #E5E7EB' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: '#9E6752' }} />
          <h3 className="text-2xl font-bold mb-3" style={{ color: '#20212B', fontFamily: 'Fraunces, serif' }}>
            Get your own AI-curated shortlist
          </h3>
          <p className="text-gray-600 mb-6">
            Describe someone in a sentence, Mementa builds a personalized gift
            shortlist you can share with your group chat in seconds.
          </p>
          <Button
            onClick={() => navigate('/browse')}
            data-testid="shared-shortlist-cta"
            style={{ background: '#9E6752', color: 'white' }}
          >
            Try it free
          </Button>
        </div>
      </div>
    </div>
  );
}

function ExpiredOrMissing({ title, message, navigate }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#FFFAF5' }}>
      <div className="text-center max-w-md">
        <Clock className="w-16 h-16 mx-auto mb-4" style={{ color: '#B0A091' }} />
        <h1 className="text-2xl font-bold mb-2" style={{ color: '#20212B', fontFamily: 'Fraunces, serif' }}>
          {title}
        </h1>
        <p className="text-gray-600 mb-6">{message}</p>
        <Button
          onClick={() => navigate('/browse')}
          style={{ background: '#9E6752', color: 'white' }}
          data-testid="shared-shortlist-fallback-cta"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Browse Mementa
        </Button>
      </div>
    </div>
  );
}
